import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaExpand, FaCompress, FaSearch } from 'react-icons/fa';
import ImageWithFallback from './ImageWithFallback';
import './ImageGallery.css';

const ImageGallery = ({ images, initialImage = 0 }) => {
  const [showModal, setShowModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(initialImage);
  const [isZoomed, setIsZoomed] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const handlePrevious = useCallback((e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setIsZoomed(false);
  }, [images.length]);

  const handleNext = useCallback((e) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    setIsZoomed(false);
  }, [images.length]);

  const handleKeyPress = useCallback((e) => {
    if (!showModal) return;
    
    if (e.key === 'ArrowLeft') handlePrevious(e);
    if (e.key === 'ArrowRight') handleNext(e);
    if (e.key === 'Escape') setShowModal(false);
  }, [showModal, handlePrevious, handleNext]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrevious();
    }
  };

  const handleImageClick = (index) => {
    setCurrentIndex(index);
    setShowModal(true);
    setIsZoomed(false);
  };

  const toggleZoom = (e) => {
    e.stopPropagation();
    setIsZoomed(!isZoomed);
  };

  if (!images || images.length === 0) {
    return (
      <div className="text-center p-4 bg-light rounded">
        <p>No images available</p>
      </div>
    );
  }

  return (
    <>
      <div className="image-gallery-grid">
        {images.slice(0, 3).map((image, index) => (
          <motion.div
            key={index}
            className={`gallery-item${index === 0 ? ' main-image' : ''}`}
            whileHover={{ scale: 1.02 }}
            onClick={() => handleImageClick(index)}
          >
            <ImageWithFallback
              src={image.url}
              alt={image.caption}
              className="w-100 h-100"
              style={{ objectFit: 'cover' }}
            />
          </motion.div>
        ))}
      </div>

      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        size="xl"
        centered
        className="gallery-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{images[currentIndex]?.caption}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div 
            className={`modal-image-container ${isZoomed ? 'zoomed' : ''}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                className="image-wrapper"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
              >
                <ImageWithFallback
                  src={images[currentIndex]?.url}
                  alt={images[currentIndex]?.caption}
                  onClick={toggleZoom}
                  className={isZoomed ? 'zoomed' : ''}
                />
              </motion.div>
            </AnimatePresence>

            <button className="gallery-nav prev" onClick={handlePrevious}>
              <FaChevronLeft />
            </button>
            <button className="gallery-nav next" onClick={handleNext}>
              <FaChevronRight />
            </button>
            <button className="zoom-button" onClick={toggleZoom}>
              {isZoomed ? <FaCompress /> : <FaExpand />}
            </button>
          </div>

          <div className="thumbnails-container">
            {images.map((image, index) => (
              <motion.div
                key={index}
                className={`thumbnail-wrapper ${index === currentIndex ? 'active' : ''}`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setCurrentIndex(index)}
              >
                <ImageWithFallback
                  src={image.url}
                  alt={image.caption}
                  className="thumbnail"
                />
                <div className="thumbnail-caption">{image.caption}</div>
              </motion.div>
            ))}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ImageGallery; 